import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faMagnifyingGlass, faPen, faTrash, faArrowLeft,
    faBuilding, faEnvelope, faPhone, faComments, faToggleOn, faToggleOff,
    faCalendarDays, faUserMinus, faKey, faRightToBracket, faXmark, faListCheck
} from '@fortawesome/free-solid-svg-icons';

import api from '../utils/api';
import { setGuestSession } from '../utils/portalApi';
import RichTextEditor from '../components/RichTextEditor';
import RichText from '../components/RichText';
import Avatar from '../components/Avatar';
import AccessExpiry from '../components/chat/AccessExpiry';
import VendorCrmLink from '../components/VendorCrmLink';
import { useRecentSearches } from '../components/RecentSearches';
import '../styles/externalUsers.css';

/**
 * Vendors: the directory of people outside the company — feature draft
 * Module 2, and the staff half of the guest role (Phase 2).
 *
 * ADMIN, HR and MANAGER see every vendor and can switch one off everywhere.
 * Everyone else sees only the vendors they manage - the ones they added,
 * invited to a group or invited to a meeting - and the server enforces that;
 * this page only draws what it is given.
 *
 * Exists because a vendor is a person, not a row in one group. Before this,
 * adding the same supplier to a second project meant typing their name and
 * email again, which gave one human two records that could disagree about who
 * they were and no way to see everywhere they had access.
 *
 * So this page owns the person — name, company, what kind of outsider they are,
 * notes — and the chat owns their access to any particular group. Nothing here
 * grants access to anything; that still happens per group, with its own link
 * and its own expiry.
 */

const TYPES = ['VENDOR', 'CLIENT', 'CONSULTANT', 'CONTRACTOR', 'OTHER'];

const TYPE_LABEL = {
    VENDOR: 'Vendor',
    CLIENT: 'Client',
    CONSULTANT: 'Consultant',
    CONTRACTOR: 'Contractor',
    OTHER: 'Other'
};

const blank = {
    name: '', email: '', company: '', phone: '', type: 'VENDOR', notes: '', sendWelcome: true,
    inviteMethod: 'email', password: ''
};

/*
 * A password the team sets for a guest and hands over. Readable when read out
 * or retyped - no 0/O or 1/l/I - and always with a letter and a number, which
 * the server requires.
 */
const LETTERS = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ';
const DIGITS = '23456789';
const randomIndex = (n) => window.crypto.getRandomValues(new Uint32Array(1))[0] % n;
const generatePassword = () => {
    const all = LETTERS + DIGITS;
    const chars = [LETTERS[randomIndex(LETTERS.length)], DIGITS[randomIndex(DIGITS.length)]];
    while (chars.length < 12) chars.push(all[randomIndex(all.length)]);
    for (let i = chars.length - 1; i > 0; i -= 1) {
        const j = randomIndex(i + 1);
        [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    return chars.join('');
};

const passwordProblem = (value) => {
    if (value.length < 8) return 'Use at least 8 characters.';
    if (!/[A-Za-z]/.test(value) || !/\d/.test(value)) return 'Use at least one letter and one number.';
    return null;
};

const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
}[c]));

/** The address and password to hand over, with a button that copies both. */
const showCredentials = (title, person, password, signInUrl) => {
    const signIn = signInUrl || `${window.location.origin}/guest/login`;
    const text = `Sign in as a guest at ${signIn}\nEmail: ${person.email}\nPassword: ${password}\nYou will be asked to choose your own password the first time.`;
    return Swal.fire({
        icon: 'success',
        title,
        html: `<p style="margin:0 0 10px">Share these with ${escapeHtml(person.name)}. This is the only time the password is shown.</p>`
            + `<pre style="text-align:left;white-space:pre-wrap;background:#f1f5f9;color:#0f172a;padding:12px;border-radius:8px;font-size:13px">${escapeHtml(text)}</pre>`,
        confirmButtonText: 'Copy and close',
        showCancelButton: true,
        cancelButtonText: 'Close',
        preConfirm: () => navigator.clipboard?.writeText(text).catch(() => { })
    });
};

const when = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

const ExternalUsers = () => {
    const navigate = useNavigate();

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const recentSearch = useRecentSearches('external-users', { value: query, onPick: setQuery });
    const [typeFilter, setTypeFilter] = useState('');
    const [showInactive, setShowInactive] = useState(false);

    // null = closed, {} = adding, {_id…} = editing
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(blank);
    const [saving, setSaving] = useState(false);

    const [detail, setDetail] = useState(null);
    // The guest whose profile is on its way, while it is.
    const [opening, setOpening] = useState(null);
    const wanted = useRef(null);

    // What this employee may do here; the server enforces all of it.
    const role = JSON.parse(localStorage.getItem('user') || 'null')?.role || 'EMPLOYEE';
    const canDelete = ['ADMIN', 'HR'].includes(role);
    const [access, setAccess] = useState({ seesAll: false, canSwitchOff: false });
    const [mineOnly, setMineOnly] = useState(false);
    useEffect(() => {
        api.get('/external-users/summary').then((res) => setAccess(res.data)).catch(() => { });
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/external-users', {
                params: {
                    q: query || undefined,
                    type: typeFilter || undefined,
                    includeInactive: showInactive ? 1 : undefined,
                    mine: mineOnly ? 1 : undefined
                }
            });
            setRows(res.data);
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not load guests.', 'error');
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [query, typeFilter, showInactive, mineOnly]);

    // Debounced, so typing into the search box is not one request per keystroke.
    useEffect(() => {
        const t = setTimeout(load, query ? 300 : 0);
        return () => clearTimeout(t);
    }, [load, query]);

    const openAdd = () => { setForm(blank); setEditing({}); };

    const openEdit = (u) => {
        setForm({
            name: u.name, email: u.email, company: u.company || '',
            phone: u.phone || '', type: u.type || 'VENDOR', notes: u.notes || ''
        });
        setEditing(u);
    };

    const save = async () => {
        if (!form.name.trim() || (!editing._id && !form.email.trim())) return;
        setSaving(true);
        try {
            if (editing._id) {
                // The email is deliberately not sent — it is the identity every
                // live invitation was mailed to, and the server refuses it.
                await api.put(`/external-users/${editing._id}`, {
                    name: form.name, company: form.company,
                    phone: form.phone, type: form.type, notes: form.notes
                });
            } else {
                const withCredentials = form.inviteMethod === 'credentials';
                const problem = withCredentials && passwordProblem(form.password);
                if (problem) {
                    Swal.fire('Password', problem, 'warning');
                    return;
                }
                const res = await api.post('/external-users', {
                    ...form,
                    password: withCredentials ? form.password : undefined
                });
                if (withCredentials) {
                    showCredentials('Guest added', res.data, form.password, res.data.signInUrl);
                } else if (form.sendWelcome) {
                    Swal.fire(res.data.welcomeSent
                        ? { icon: 'success', title: 'Guest added', text: `${res.data.name} has been emailed how to sign in as a guest.` }
                        : { icon: 'warning', title: 'Guest added', text: 'The welcome email could not be sent. They can still sign in at /guest/login with their email.' });
                }
            }
            setEditing(null);
            load();
        } catch (err) {
            const body = err.response?.data;
            if (!editing._id && err.response?.status === 409 && body?.existing) {
                await offerExisting(body);
            } else {
                Swal.fire('Error', body?.message || 'Could not save.', 'error');
            }
        } finally {
            setSaving(false);
        }
    };

    /**
     * The email already belongs to somebody in the directory.
     *
     * Usually they were "deleted" earlier: a vendor with any history is kept
     * and switched off rather than erased, so the address is still taken.
     * Adding them again means bringing that record back, not making a second.
     */
    const offerExisting = async (body) => {
        const person = body.existing;
        const withCredentials = form.inviteMethod === 'credentials';
        if (body.switchedOff && !body.canSwitchOn) {
            return Swal.fire('Switched off', `${body.message} Ask an admin, HR or a manager to switch them back on.`, 'info');
        }
        const ask = await Swal.fire({
            title: body.switchedOff ? `Bring back ${person.name}?` : `${person.name} is already here`,
            text: withCredentials
                ? `${body.message} ${body.switchedOff ? 'Switch them back on and set' : 'Set'} the password you typed for them instead?`
                : body.switchedOff
                    ? `${body.message} Switch them back on and email them how to sign in?`
                    : `${body.message} Email them how to sign in as a guest?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: withCredentials
                ? (body.switchedOff ? 'Switch on and set password' : 'Set password')
                : (body.switchedOff ? 'Switch on and email' : 'Send sign-in email')
        });
        if (!ask.isConfirmed) return;
        try {
            if (body.switchedOff) {
                // What was just typed into the form wins over the old record.
                await api.put(`/external-users/${person._id}`, {
                    isActive: true,
                    name: form.name || person.name,
                    company: form.company || person.company || '',
                    phone: form.phone || person.phone || '',
                    type: form.type || person.type
                });
            }
            if (withCredentials) {
                const set = await api.post(`/external-users/${person._id}/password`, { password: form.password });
                setEditing(null);
                load();
                showCredentials('Password set', person, form.password, set.data.signInUrl);
                return;
            }
            await api.post(`/external-users/${person._id}/welcome`);
            setEditing(null);
            load();
            Swal.fire('Done', `${form.name || person.name} has been emailed how to sign in as a guest.`, 'success');
        } catch (err) {
            load();
            Swal.fire('Error', err.response?.data?.message || 'Could not finish that.', 'error');
        }
    };

    const resendWelcome = async (person) => {
        try {
            await api.post(`/external-users/${person._id}/welcome`);
            Swal.fire('Sent', `${person.name} has been emailed how to sign in as a guest.`, 'success');
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not send the email.', 'error');
        }
    };

    /** Sets a password for a guest, for the team to hand over. */
    const setPassword = async (person) => {
        const ask = await Swal.fire({
            title: `Set a password for ${person.name}`,
            text: person.hasPassword
                ? 'This replaces their current password and signs them out everywhere. They will be asked to choose their own the next time they sign in.'
                : 'They can then sign in with their email and this password. They will be asked to choose their own the first time.',
            input: 'text',
            inputValue: generatePassword(),
            inputAttributes: { autocomplete: 'off', spellcheck: 'false' },
            showCancelButton: true,
            confirmButtonText: 'Set password',
            inputValidator: (value) => passwordProblem(value || '')
        });
        if (!ask.isConfirmed) return;
        try {
            const set = await api.post(`/external-users/${person._id}/password`, { password: ask.value });
            openDetail(person);
            showCredentials('Password set', person, ask.value, set.data.signInUrl);
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not set the password.', 'error');
        }
    };

    /** Admin only: opens the guest portal, in a new tab, as this guest sees it. */
    const loginAs = async (person) => {
        // Opened now, inside the click, so the browser does not block it as a pop-up.
        const tab = window.open('', '_blank');
        try {
            const { data } = await api.post(`/external-users/${person._id}/login-as`);
            setGuestSession(data.token);
            if (tab) tab.location.href = '/guest/chats';
            else window.location.href = '/guest/chats';
        } catch (err) {
            tab?.close();
            Swal.fire('Error', err.response?.data?.message || 'Could not sign in as this guest.', 'error');
        }
    };

    const toggleActive = async (u) => {
        if (u.isActive) {
            const ok = await Swal.fire({
                title: `Switch off ${u.name}?`,
                text: 'They lose access to every chat and meeting at once, including other people\'s, and are signed out now. You can switch them back on later.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#dc2626',
                confirmButtonText: 'Switch off'
            });
            if (!ok.isConfirmed) return;
        }
        try {
            await api.put(`/external-users/${u._id}`, { isActive: !u.isActive });
            load();
            if (detail?.person._id === u._id) openDetail(u);
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not change that.', 'error');
        }
    };

    const remove = async (u) => {
        const ok = await Swal.fire({
            title: `Remove ${u.name}?`,
            html: u.groupCount
                ? `They are in <b>${u.groupCount}</b> conversation${u.groupCount === 1 ? '' : 's'}. `
                  + 'Their record and their messages are kept, and their access is switched off everywhere.'
                : 'They have never been added to a conversation, so their record will be deleted.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            confirmButtonText: 'Remove'
        });
        if (!ok.isConfirmed) return;

        try {
            const res = await api.delete(`/external-users/${u._id}`);
            if (res.data.deactivated) {
                Swal.fire({ icon: 'info', title: 'Access switched off', text: res.data.message });
            }
            load();
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not remove them.', 'error');
        }
    };

    const openDetail = async (u) => {
        recentSearch.save();
        // The popup opens at once with what the list already knows, so a click
        // is answered straight away; the rest fills in when it arrives.
        setOpening(u);
        wanted.current = u._id;
        try {
            const res = await api.get(`/external-users/${u._id}`);
            // Closed while loading, or another profile asked for since: not this one.
            if (wanted.current === u._id) setDetail(res.data);
        } catch (err) {
            Swal.fire('Error', 'Could not load that profile.', 'error');
        } finally {
            setOpening((current) => (current?._id === u._id ? null : current));
        }
    };

    // Out of one group only - their other groups and meetings are untouched.
    const removeFromGroup = async (m) => {
        const ok = await Swal.fire({
            title: `Remove ${detail.person.name} from ${m.conversation?.name || 'this group'}?`,
            text: 'They stop seeing this group straight away. Their other groups and meetings are not affected.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            confirmButtonText: 'Remove'
        });
        if (!ok.isConfirmed) return;
        try {
            await api.delete(`/conversations/${m.conversation._id}/externals/${m._id}`);
            openDetail(detail.person);
            load();
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not remove them.', 'error');
        }
    };

    return (
        <div className="exu">
            <div className="exu-head">
                <button className="exu-back" onClick={() => navigate('/chats')}>
                    <FontAwesomeIcon icon={faArrowLeft} /> Back to chats
                </button>

                <div className="exu-title">
                    <h2>Guests</h2>
                    <p>
                        {access.seesAll
                            ? 'Everyone outside the company - vendors, clients and consultants. '
                            : 'The people outside the company you work with. '}
                        Add somebody here once, then add them to chats and meetings by their email.
                        They sign in as a guest and see only what they are invited to.
                    </p>
                </div>

                <button className="exu-add" onClick={openAdd}>
                    <FontAwesomeIcon icon={faPlus} /> Add guest
                </button>
            </div>

            <div className="exu-toolbar">
                <div className="exu-search">
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                    <input
                        {...recentSearch.inputProps}
                        value={query}
                        placeholder="Search by name, email or company"
                        onChange={(e) => setQuery(e.target.value)}
                    />
                    {recentSearch.menu}
                </div>

                <div className="exu-filters">
                    <button
                        className={!typeFilter ? 'active' : ''}
                        onClick={() => setTypeFilter('')}
                    >
                        All
                    </button>
                    {TYPES.map((t) => (
                        <button
                            key={t}
                            className={typeFilter === t ? 'active' : ''}
                            onClick={() => setTypeFilter(t)}
                        >
                            {TYPE_LABEL[t]}
                        </button>
                    ))}
                </div>

                {access.seesAll && (
                    <label className="exu-toggle">
                        <input type="checkbox" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} />
                        Only mine
                    </label>
                )}

                <label className="exu-toggle">
                    <input
                        type="checkbox"
                        checked={showInactive}
                        onChange={(e) => setShowInactive(e.target.checked)}
                    />
                    Show switched off
                </label>
            </div>

            {loading && <div className="exu-empty">Loading…</div>}

            {!loading && !rows.length && (
                <div className="exu-empty">
                    {query || typeFilter
                        ? 'Nobody matches that.'
                        : 'No guests yet. Add one here, or invite somebody to a chat or meeting by email.'}
                </div>
            )}

            <div className="exu-grid">
                {rows.map((u) => (
                    <div className={`exu-card ${u.isActive ? '' : 'off'}`} key={u._id}>
                        <div className="exu-card-top">
                            <div className="exu-avatar">
                                {u.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
                            </div>
                            <div className="exu-card-id">
                                <h4 onClick={() => openDetail(u)}>{u.name}</h4>
                                <span className="exu-code">{u.externalId}</span>
                            </div>
                            <span className={`exu-type t-${u.type}`}>{TYPE_LABEL[u.type]}</span>
                        </div>

                        <div className="exu-card-meta">
                            {u.company && (
                                <span><FontAwesomeIcon icon={faBuilding} /> {u.company}</span>
                            )}
                            <span><FontAwesomeIcon icon={faEnvelope} /> {u.email}</span>
                            {u.phone && <span><FontAwesomeIcon icon={faPhone} /> {u.phone}</span>}
                        </div>

                        <div className="exu-card-foot">
                            <button className="exu-link" onClick={() => openDetail(u)}>
                                <FontAwesomeIcon icon={faComments} />
                                {u.groupCount
                                    ? `In ${u.groupCount} conversation${u.groupCount === 1 ? '' : 's'}`
                                    : 'Not in any conversation'}
                            </button>

                            <div className="exu-card-actions">
                                {access.canSwitchOff && (
                                    <button
                                        onClick={() => toggleActive(u)}
                                        title={u.isActive ? 'Switch off access everywhere' : 'Switch access back on'}
                                    >
                                        <FontAwesomeIcon icon={u.isActive ? faToggleOn : faToggleOff} />
                                    </button>
                                )}
                                <button onClick={() => openEdit(u)} title="Edit">
                                    <FontAwesomeIcon icon={faPen} />
                                </button>
                                {canDelete && (
                                    <button className="danger" onClick={() => remove(u)} title="Remove">
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                )}
                            </div>
                        </div>

                        {!u.isActive && <div className="exu-off-flag">Access switched off everywhere</div>}
                    </div>
                ))}
            </div>

            {/* ------------------------- ADD / EDIT ------------------------- */}
            {editing && (
                <div className="exu-modal-back" onClick={() => setEditing(null)}>
                    <div className="exu-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>{editing._id ? `Edit ${editing.name}` : 'Add a guest'}</h3>

                        <label>Name</label>
                        <input
                            value={form.name}
                            autoFocus
                            placeholder="Ramesh Kumar"
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                        />

                        <label>Email</label>
                        <input
                            type="email"
                            value={form.email}
                            disabled={Boolean(editing._id)}
                            placeholder="ramesh@vendor.com"
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                        />
                        {editing._id && (
                            <p className="exu-note">
                                The email cannot be changed — it is what every invitation link
                                already sent to this person was addressed to. Somebody who has
                                genuinely changed address needs a new record.
                            </p>
                        )}

                        <label>They are a</label>
                        <div className="exu-typepick">
                            {TYPES.map((t) => (
                                <button
                                    key={t}
                                    type="button"
                                    className={form.type === t ? 'active' : ''}
                                    onClick={() => setForm({ ...form, type: t })}
                                >
                                    {TYPE_LABEL[t]}
                                </button>
                            ))}
                        </div>

                        <label>Company</label>
                        <input
                            value={form.company}
                            placeholder="VendorCo Pvt Ltd"
                            onChange={(e) => setForm({ ...form, company: e.target.value })}
                        />

                        <label>Phone (optional)</label>
                        <input
                            value={form.phone}
                            placeholder="+91 98765 43210"
                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        />

                        <label>Notes (optional)</label>
                        <RichTextEditor
                            rows={3}
                            value={form.notes}
                            placeholder="What they supply, who owns the relationship, anything the next person should know."
                            onChange={(v) => setForm((f) => ({ ...f, notes: v }))}
                        />

                        {!editing._id && (
                            <>
                                <label>How they get access</label>
                                <div className="exu-typepick">
                                    <button
                                        type="button"
                                        className={form.inviteMethod === 'email' ? 'active' : ''}
                                        onClick={() => setForm({ ...form, inviteMethod: 'email' })}
                                    >
                                        Invite by email
                                    </button>
                                    <button
                                        type="button"
                                        className={form.inviteMethod === 'credentials' ? 'active' : ''}
                                        onClick={() => setForm({ ...form, inviteMethod: 'credentials', password: form.password || generatePassword() })}
                                    >
                                        Set a password
                                    </button>
                                </div>
                                {form.inviteMethod === 'credentials' && (
                                    <>
                                        <div className="exu-password">
                                            <input
                                                value={form.password}
                                                autoComplete="off"
                                                spellCheck={false}
                                                placeholder="At least 8 characters, a letter and a number"
                                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                                            />
                                            <button type="button" className="exu-btn" onClick={() => setForm({ ...form, password: generatePassword() })}>
                                                Generate
                                            </button>
                                        </div>
                                        <p className="exu-note">
                                            No email is sent. You will be shown the email and password to share
                                            with them yourself, and they will be asked to choose their own password
                                            the first time they sign in. Use their real email address: chat, task
                                            and meeting notifications still go there.
                                        </p>
                                    </>
                                )}
                            </>
                        )}

                        {!editing._id && form.inviteMethod === 'email' && (
                            <label className="exu-toggle exu-welcome">
                                <input
                                    type="checkbox"
                                    checked={form.sendWelcome}
                                    onChange={(e) => setForm({ ...form, sendWelcome: e.target.checked })}
                                />
                                Email them how to sign in as a guest
                            </label>
                        )}

                        <div className="exu-modal-foot">
                            <button className="exu-btn" onClick={() => setEditing(null)}>Cancel</button>
                            <button
                                className="exu-btn primary"
                                onClick={save}
                                disabled={saving || !form.name.trim() || (!editing._id && !form.email.trim())
                                    || (!editing._id && form.inviteMethod === 'credentials' && !form.password)}
                            >
                                {saving ? 'Saving…' : (editing._id ? 'Save changes' : 'Add them')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --------------------------- PROFILE --------------------------- */}
            {(detail || opening) && (() => {
                const person = detail?.person || opening;
                const close = () => { wanted.current = null; setOpening(null); setDetail(null); };
                return (
                    <div className="exu-modal-back" onClick={close}>
                        <div className="exu-modal exu-pf" role="dialog" aria-label={person.name} onClick={(e) => e.stopPropagation()}>
                            <header className="exu-pf-head">
                                <Avatar name={person.name} profilePic={person.profilePic} className="exu-pf-avatar" />
                                <div className="exu-pf-who">
                                    <h3>{person.name}</h3>
                                    <p>{[person.company, person.email, person.externalId].filter(Boolean).join(' · ')}</p>
                                    {detail && (
                                        <div className="exu-pf-chips">
                                            <span className={`exu-chip ${person.isActive ? 'ok' : 'off'}`}>
                                                {person.isActive ? 'Guest access on' : 'Switched off'}
                                            </span>
                                            <span className="exu-chip">
                                                {person.lastLoginAt ? `Signed in ${when(person.lastLoginAt)}` : 'Never signed in'}
                                            </span>
                                            {person.hasPassword && <span className="exu-chip"><FontAwesomeIcon icon={faKey} /> Has a password</span>}
                                            {person.createdBy?.name && <span className="exu-chip">Added by {person.createdBy.name}</span>}
                                        </div>
                                    )}
                                </div>
                                <button type="button" className="exu-pf-close" aria-label="Close" onClick={close}>
                                    <FontAwesomeIcon icon={faXmark} />
                                </button>
                            </header>

                            {!detail ? (
                                <div className="exu-pf-body">
                                    <div className="exu-pf-loading"><span className="exu-spinner" /> Loading their conversations, meetings and tasks…</div>
                                </div>
                            ) : (
                                <>
                                    <div className="exu-pf-actions">
                                        {person.isActive && detail.canLoginAs && (
                                            <button className="exu-btn primary" onClick={() => loginAs(person)}>
                                                <FontAwesomeIcon icon={faRightToBracket} /> Login as guest
                                            </button>
                                        )}
                                        {person.isActive && (
                                            <button className="exu-btn" onClick={() => resendWelcome(person)}>
                                                <FontAwesomeIcon icon={faEnvelope} /> Resend sign-in email
                                            </button>
                                        )}
                                        {person.isActive && detail.canSetPassword && (
                                            <button className="exu-btn" onClick={() => setPassword(person)}>
                                                <FontAwesomeIcon icon={faKey} /> {person.hasPassword ? 'Reset password' : 'Set password'}
                                            </button>
                                        )}
                                        {detail.canSwitchOff && (
                                            <button className={`exu-btn ${person.isActive ? 'danger' : ''}`} onClick={() => toggleActive(person)}>
                                                <FontAwesomeIcon icon={person.isActive ? faToggleOff : faToggleOn} />
                                                {person.isActive ? ' Switch off everywhere' : ' Switch back on'}
                                            </button>
                                        )}
                                    </div>

                                    <div className="exu-pf-body">
                                        <VendorCrmLink
                                            vendorId={person._id}
                                            crm={detail.crm}
                                            onChanged={() => { openDetail(person); load(); }}
                                        />

                                        {person.notes && <RichText text={person.notes} className="exu-notes-body" />}

                                        <section className="exu-pf-section">
                                            <h5>
                                                <FontAwesomeIcon icon={faComments} /> Conversations
                                                <span className="exu-pf-count">{detail.memberships.length}</span>
                                                {detail.messageCount > 0 && <small>{detail.messageCount} message{detail.messageCount === 1 ? '' : 's'} sent</small>}
                                            </h5>
                                            {!detail.memberships.length && <p className="exu-pf-empty">Not in any conversation yet.</p>}
                                            {detail.memberships.map((m) => (
                                                <div className="exu-pf-row" key={m._id}>
                                                    <div className="exu-pf-row-main">
                                                        <strong>{m.conversation?.name || 'A conversation'}</strong>
                                                        <span>
                                                            {m.role === 'viewer' ? 'View only' : 'Can reply and upload'}
                                                            {' · '}{m.shareHistory ? 'Full history' : 'Messages since invitation'}
                                                        </span>
                                                        <span>
                                                            Invited by {m.invitedBy?.name || 'someone'}
                                                            {m.invitedAt && ` on ${new Date(m.invitedAt).toLocaleDateString('en-GB')}`}
                                                        </span>
                                                        {/* Only an invitation not yet accepted runs out. */}
                                                        {['invited', 'pending'].includes(m.status) && <AccessExpiry expiresAt={m.expiresAt} />}
                                                    </div>
                                                    <div className="exu-pf-row-side">
                                                        <span className={`exu-chip ${m.isActive ? 'ok' : 'off'}`}>{m.isActive ? 'Active' : m.status}</span>
                                                        <div className="exu-pf-row-actions">
                                                            {m.conversation?._id && (
                                                                <button className="exu-btn small" onClick={() => navigate(`/chats/${m.conversation._id}`)}>Open</button>
                                                            )}
                                                            {m.canRemove && (
                                                                <button className="exu-btn small danger" onClick={() => removeFromGroup(m)}>
                                                                    <FontAwesomeIcon icon={faUserMinus} /> Remove from group
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </section>

                                        <section className="exu-pf-section">
                                            <h5>
                                                <FontAwesomeIcon icon={faCalendarDays} /> Meetings
                                                <span className="exu-pf-count">{(detail.meetings || []).length}</span>
                                            </h5>
                                            {!detail.meetings?.length && <p className="exu-pf-empty">Not invited to any meetings.</p>}
                                            {(detail.meetings || []).map((meeting) => (
                                                <div className="exu-pf-row" key={meeting._id}>
                                                    <div className="exu-pf-row-main">
                                                        <strong>{meeting.title}</strong>
                                                        <span>{when(meeting.startsAt)}{meeting.organizer && ` · Organised by ${meeting.organizer}`}</span>
                                                    </div>
                                                    <div className="exu-pf-row-side">
                                                        <span className={`exu-chip ${meeting.rsvp === 'accepted' ? 'ok' : 'off'}`}>{meeting.rsvp}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </section>

                                        <section className="exu-pf-section">
                                            <h5>
                                                <FontAwesomeIcon icon={faListCheck} /> {detail.canSwitchOff ? 'Tasks' : 'Tasks you assigned'}
                                                <span className="exu-pf-count">{(detail.tasks || []).length}</span>
                                            </h5>
                                            {!detail.tasks?.length && <p className="exu-pf-empty">No tasks.</p>}
                                            {(detail.tasks || []).map((t) => (
                                                <div className="exu-pf-row" key={t._id}>
                                                    <div className="exu-pf-row-main">
                                                        <strong>{t.title}</strong>
                                                        <span>
                                                            {t.status === 'Completed' && t.completedAt ? `Completed ${when(t.completedAt)}` : `Due ${when(t.dueAt)}`}
                                                            {t.assignedBy && ` · from ${t.assignedBy}`}
                                                        </span>
                                                    </div>
                                                    <div className="exu-pf-row-side">
                                                        <span className={`exu-chip ${t.status === 'Completed' ? 'ok' : 'off'}`}>{t.status}</span>
                                                        {t.canOpen && (
                                                            <div className="exu-pf-row-actions">
                                                                <button className="exu-btn small" onClick={() => navigate(`/task/${t._id}`)}>Open</button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </section>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

export default ExternalUsers;
